/**
 * PATH: packages/ui/src/kanban/components/settings-home/chrome.tsx
 * FILE_ID: UI-KANBAN-SETTINGS-CHROME-001
 * PURPOSE: Shared settings-home chrome — nav, section shell, apply-to, rows.
 * ROLE IN ARCHITECTURE: Presentational leaves for the settings home.
 * MAIN EXPORTS: SettingsNav, SectionShell, RowButton, OptionStrip
 * NON-RESPONSIBILITIES: Does not persist prefs or own page bodies.
 * NOTES FOR FUTURE AI: Apply-to switches left-nav pages. Do not add a second density row on Board.
 */
import type { ReactNode } from 'react'

import { SETTINGS_NAV, type SettingsPage } from './nav'

export function SettingsNav({
  page,
  onPage,
  groups,
  compact = false,
  edge = 'start',
}: {
  page: SettingsPage
  onPage: (id: SettingsPage) => void
  groups?: Array<'board' | 'kanban'>
  compact?: boolean
  edge?: 'start' | 'end'
}) {
  const visible = groups
    ? SETTINGS_NAV.filter((group) => groups.includes(group.id))
    : SETTINGS_NAV
  return (
    <nav
      data-testid="settings-home-nav"
      className={[
        'h-full overflow-y-auto bg-neutral-50 px-2 py-3 dark:bg-zinc-950',
        edge === 'end'
          ? 'border-l border-neutral-100 dark:border-zinc-800'
          : 'border-r border-neutral-100 dark:border-zinc-800',
      ].join(' ')}
    >
      {visible.map((group) => (
        <div key={group.id} className="mb-4 last:mb-0">
          <p className="px-2 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
            {group.title}
          </p>
          {compact ? null : (
            <p className="mb-1 px-2 text-[11px] leading-4 text-neutral-400">{group.hint}</p>
          )}
          <div className="space-y-0.5">
            {group.items.map((item) => {
              const on = page === item.id
              return (
                <button
                  key={item.id}
                  type="button"
                  data-testid={`settings-nav-${item.id}`}
                  aria-current={on ? 'page' : undefined}
                  onClick={() => onPage(item.id)}
                  className={[
                    'flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left',
                    on
                      ? 'bg-white text-neutral-900 shadow-sm dark:bg-zinc-900 dark:text-zinc-100'
                      : 'text-neutral-600 hover:bg-white/70 dark:text-zinc-300 dark:hover:bg-zinc-900/70',
                  ].join(' ')}
                >
                  <span>
                    <span className="block text-[13px] font-medium leading-5">{item.title}</span>
                    {compact ? null : (
                      <span className="block text-[11px] leading-4 text-neutral-400">{item.hint}</span>
                    )}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      ))}
    </nav>
  )
}

export function SectionShell({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: ReactNode
}) {
  return (
    <section className="border-b border-neutral-200 px-4 py-3 dark:border-zinc-800">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-zinc-400">
        {title}
      </div>
      {hint && (
        <p className="mb-2 text-[11px] text-neutral-400 dark:text-zinc-500">{hint}</p>
      )}
      {children}
    </section>
  )
}

export function RowButton({
  label,
  danger,
  onClick,
  testId,
}: {
  label: string
  danger?: boolean
  onClick: () => void
  testId?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={[
        'flex w-full items-center rounded px-2 py-1.5 text-left text-xs',
        danger
          ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30'
          : 'text-neutral-700 hover:bg-neutral-100 dark:text-zinc-200 dark:hover:bg-zinc-900',
      ].join(' ')}
    >
      {label}
    </button>
  )
}

export function OptionStrip<T extends string>({
  label,
  value,
  options,
  onChange,
  testId,
}: {
  label: string
  value: T
  options: readonly { id: T; title: string; description?: string }[]
  onChange: (id: T) => void
  testId?: string
}) {
  return (
    <div className="mb-3" data-testid={testId}>
      <div className="mb-1 text-xs text-neutral-600 dark:text-zinc-300">{label}</div>
      <div className="flex flex-wrap gap-1">
        {options.map((option) => {
          const on = value === option.id
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={on}
              data-testid={`${testId ?? 'option'}-${option.id}`}
              onClick={() => onChange(option.id)}
              className={[
                'min-w-[7.5rem] flex-1 rounded-md border px-2.5 py-1.5 text-left',
                on
                  ? 'border-neutral-800 bg-neutral-800 text-white dark:border-zinc-200 dark:bg-zinc-200 dark:text-zinc-900'
                  : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-200',
              ].join(' ')}
            >
              <span className="block text-[12px] font-medium">{option.title}</span>
              {option.description && (
                <span className="mt-0.5 block text-[11px] opacity-80">{option.description}</span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function SettingsActivityUndo({ onUndo }: { onUndo: () => void }) {
  return (
    <SectionShell title="Activity" hint="Undo the last settings change.">
      <RowButton label="Undo last change" testId="settings-undo" onClick={onUndo} />
    </SectionShell>
  )
}

export const APPLY_TO_OPTIONS = [
  {
    id: 'board' as const,
    title: 'This board only',
    description: 'This board uses this recipe. Other boards keep the All Kanban default.',
  },
  {
    id: 'all' as const,
    title: 'All boards',
    description: 'Every board, including this one, uses this recipe.',
  },
]
